import React, { useState, useRef, useEffect } from 'react'
import _ from 'lodash'
import { Button, Input, message } from 'antd'
import ReactQuill from 'react-quill'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'
import 'react-quill/dist/quill.snow.css'
import styled from 'styled-components'
import classNames from 'classnames'
import ConfirmPopUp from '../../UI/extraInformation/confirm'
import { refineAIRecommendation } from '../../../api'
const { TextArea } = Input

const RefineSection = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
`

const SectionTitle = styled.h4`
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
`

const PromptArea = styled(TextArea)`
  font-size: 13px;
  margin-bottom: 20px;
  border-radius: 6px;
  border: 1px solid #ced4da;
`

const RecommendationContainer = styled.div`
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
`

const RefinedRecommendationContainer = styled(RecommendationContainer)`
  background-color: #e6f7ff;
  border-color: #91d5ff;
`

const EditContainer = styled.div`
  border: ${(props) => (props.editing ? '1px solid #ccc' : 'none')};

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid #ccc;
  }
  .ql-container {
    border: none;
  }

  // .custom-font {
  //   font-family: ${(props) => props.fontFamily || 'Gilroy'};
  //   font-size: ${(props) => props.fontSize || '15px'};
  //   color: ${(props) => props.fontColor || 'rgba(112, 128, 142, 1)'};
  //   &.placeholder {
  //     color: rgba(169, 169, 169, 0.7);
  //   }
  // }
  .custom-font {
    font-family: ${(props) => props.fontFamily || 'Gilroy'};
    font-size: ${(props) => props.fontSize || '15px'};
    color: ${(props) => props.fontColor || 'rgba(112, 128, 142, 1)'};
    white-space: pre-wrap;
    word-wrap: break-word;

    &.placeholder {
      // color: rgba(169, 169, 169, 0.7);
    }
    // Style the HTML output from Quill
    p {
      margin-bottom: 1em;
    }
    ul,
    ol {
      margin-left: 1.5em;
      margin-bottom: 1em;
    }
    li {
      margin-bottom: 0.5em;
    }
    strong {
      font-weight: bold;
    }
    em {
      font-style: italic;
    }
    // Style the HTML output from Quill
    p {
      margin-bottom: 1em;
    }
    ul,
    ol {
      margin-left: 1.5em;
      margin-bottom: 1em;
    }
    li {
      margin-bottom: 0.5em;
    }
    strong {
      font-weight: bold;
    }
    em {
      font-style: italic;
    }
  }
  .submit-button {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    padding: 17px;
    button {
      color: #264382;
    }
  }
`

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link'
]

export default function EditableText(props) {
  const {
    className,
    value,
    editing,
    setEditing,
    fontStyle = {},
    onSave,
    placeholder = '',
    onAutosave
  } = props
  const [text, setText] = useState(value)
  const originalContentRef = useRef(value)

  useEffect(() => {
    setText(value)
    originalContentRef.current = value
  }, [value])

  useEffect(() => {
    if (editing) {
      if (!originalContentRef.current) {
        originalContentRef.current = value
      }
    } else {
      originalContentRef.current = null
    }
  }, [editing])

  return (
    <EditContainer
      className={classNames(className)}
      editing={editing}
      {...fontStyle}
    >
      {editing ? (
        <RichTextEditor
          simpleMode={props.simpleMode || false}
          text={text}
          originalContentRef={originalContentRef}
          placeholder={placeholder}
          onCancel={() => setEditing(false)}
          onSave={(text) => {
            setText(text)
            onSave && onSave(text)
            setEditing(false)
          }}
          onAutosave={onAutosave}
          setText={setText}
          arDetail={props.arDetail}
          person={props.person}
          recommendationType={props.recommendationType}
        />
      ) : (
        <div
          dangerouslySetInnerHTML={{
            __html: value || placeholder
          }}
          className={classNames('custom-font', { placeholder: !value })}
        />
      )}
    </EditContainer>
  )
}

const RichTextEditor = (props) => {
  const { text, onCancel, onSave, placeholder = 'Leave a message', onAutosave, setText, originalContentRef, arDetail, person, recommendationType } = props
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [isAutosaving, setIsAutosaving] = useState(false)
  const autosaveTimeoutRef = useRef(null)
  const lastSavedContentRef = useRef(text)
  let editorRef = useRef(null)

  // AI Refinement states
  const [refinementPrompt, setRefinementPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [refinedContent, setRefinedContent] = useState('')
  const [showAcceptDecline, setShowAcceptDecline] = useState(false)

  const resetToSaved = () => {
    setText(originalContentRef.current)
    onSave(originalContentRef.current)
    onCancel()
  }

  const handleRefine = async () => {
    if (!refinementPrompt.trim()) {
      message.error('Please enter a refinement prompt')
      return
    }

    setLoading(true)
    try {
      const response = await refineAIRecommendation(
        person.id,
        arDetail.id,
        recommendationType,
        text,
        refinementPrompt,
      )

      const content = response.refined_content || ''
      setRefinedContent(content)
      setShowAcceptDecline(true)
    } catch (error) {
      message.error('Failed to refine recommendation. Please try again.')
      console.error('Error refining recommendation:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = () => {
    setText(refinedContent)
    setShowAcceptDecline(false)
    setRefinedContent('')
    setRefinementPrompt('')
  }

  const handleDecline = () => {
    setShowAcceptDecline(false)
    setRefinedContent('')
    setRefinementPrompt('')
  }

  const modules = {
    toolbar: {
      container: props.simpleMode
        ? [['bold', 'italic', 'underline', 'strike', 'blockquote']]
        : [
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [
              { list: 'ordered' },
              { list: 'bullet' },
              { indent: '-1' },
              { indent: '+1' }
            ],
            ['link']
          ]
    }
  }

  return (
    <>
      {!showAcceptDecline ? (
        <>
          <ReactQuill
            ref={editorRef}
            theme="snow"
            className="custom-font"
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            defaultValue={text}
            onChange={handleTextChange}
          />
          <div className="submit-button">
            <Button type="text" onClick={() => setCancelConfirm(true)}>
              CANCEL
            </Button>
            <Button
              type="text"
              onClick={submit}
              disabled={isAutosaving}
              loading={isAutosaving}
            >
              {isAutosaving ? 'SAVING ...' : 'CLOSE'}
            </Button>
          </div>
          <RefineSection style={{ marginTop: '24px' }}>
            <SectionTitle>Refinement Prompt:</SectionTitle>
            <PromptArea
              value={refinementPrompt}
              onChange={(e) => setRefinementPrompt(e.target.value)}
              placeholder="Enter your refinement request (e.g., 'Make this more detailed', 'Focus on nutrition', 'Add exercise recommendations')"
              rows={4}
            />
            <Button
              type="primary"
              onClick={handleRefine}
              loading={loading}
              disabled={!refinementPrompt.trim() || !text || text.trim() === ''}
              style={{ marginBottom: '16px' }}
            >
              Refine with AI
            </Button>
          </RefineSection>
        </>
      ) : (
        <>
          <RefineSection>
            <SectionTitle>Original Content:</SectionTitle>
            <RecommendationContainer
              dangerouslySetInnerHTML={{
                __html: text || 'No content available'
              }}
            />
          </RefineSection>

          <RefineSection>
            <SectionTitle>Refined Content:</SectionTitle>
            <RefinedRecommendationContainer
              dangerouslySetInnerHTML={{
                __html: refinedContent || 'No refined content available'
              }}
            />
          </RefineSection>

          <div className="submit-button">
            <Button onClick={handleDecline} style={{ marginRight: '12px' }}>Decline</Button>
            <Button type="primary" onClick={handleAccept} style={{ color: 'white' }}>
              Accept
            </Button>
          </div>
        </>
      )}

      <ConfirmPopUp
        title="Discard changes"
        content="Recent changes have been automatically saved. This will revert to the original content that was present when editing started. Are you sure you want to continue?"
        okText="DISCARD"
        visible={cancelConfirm}
        handleCancel={() => setCancelConfirm(false)}
        handleOk={resetToSaved}
      />
    </>
  )

  function getEditor() {
    const getEditor = _.get(editorRef, 'current.getEditor')
    const quillEditor = getEditor && getEditor()
    let delta, range
    if (quillEditor) {
      delta = quillEditor.getContents()
      range = quillEditor.getSelection()
    }
    return { quillEditor, delta, range }
  }

  function handleTextChange() {
    if (onAutosave) {
      const { quillEditor, delta } = getEditor()
      if (quillEditor && delta && delta.ops) {
        const converter = new QuillDeltaToHtmlConverter(delta.ops, {})
        const currentHtml = converter.convert()

        if (currentHtml !== lastSavedContentRef.current) {

          if (autosaveTimeoutRef.current) {
            clearTimeout(autosaveTimeoutRef.current)
          }

          setIsAutosaving(true)
          autosaveTimeoutRef.current = setTimeout(async () => {
            try {
              await onAutosave(currentHtml)
              lastSavedContentRef.current = currentHtml
              setIsAutosaving(false)
            } catch (error) {
              console.error('EditableText Autosave - Failed:', error)
              setIsAutosaving(false)
            }
          }, 2000)
        }
      } else {
        console.warn('EditableText Autosave - Editor or delta not available')
      }
    }
  }

  async function submit() {
    const { quillEditor, delta } = getEditor()
    const converter = new QuillDeltaToHtmlConverter(delta.ops, {})
    const richText = converter.convert()
    const text = quillEditor.getText()
    if (!text.trim() && !_.includes(richText, '<img')) {
      return
    }
    onSave(richText)
  }
}
